import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, KeyRound, Users, ShieldCheck, HelpCircle, Mail } from "lucide-react";
import { Footer } from "./Footer";

const EMAIL = "saferidecarppool@gmail.com";

const TOPICS = [
  {
    icon: KeyRound,
    title: "Account access",
    body: "Trouble logging in, verifying your identity, resetting your password, or recovering your account.",
    subject: "Account access help",
    color: "#2340b5",
  },
  {
    icon: Users,
    title: "Issues with drivers or riders",
    body: "Report conduct, safety concerns, no-shows, or disputes involving another driver or rider.",
    subject: "Report an issue with a driver or rider",
    color: "#e4002b",
  },
  {
    icon: ShieldCheck,
    title: "Privacy & data",
    body: "Access, correct, or delete your personal information, or ask a question about how your data is handled.",
    subject: "Privacy & data request",
    color: "#2340b5",
  },
  {
    icon: HelpCircle,
    title: "Other issues",
    body: "Payments, bookings, feedback, partnership enquiries, or anything else we can help with.",
    subject: "General enquiry",
    color: "#fdb913",
  },
];

export default function ContactPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 border-b border-[#12142b]/10 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1000px] items-center justify-between px-6 py-4 lg:px-8">
          <Link to="/" data-testid="contact-logo" className="flex items-center gap-2.5">
            <img src="/saferide-logo.png" alt="SafeRide" className="h-8 w-auto" />
          </Link>
          <Link
            to="/"
            data-testid="contact-back-home"
            className="group inline-flex items-center gap-2 font-display text-sm font-medium text-[#565b73] transition-colors hover:text-[#2340b5]"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" strokeWidth={1.75} />
            Back home
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-[1000px] px-6 pb-12 pt-20 lg:px-8 lg:pt-28">
        <p className="mb-6 inline-flex items-center gap-3 font-display text-xs font-semibold uppercase tracking-[0.22em] text-[#2340b5]">
          <span className="h-px w-10 bg-[#2340b5]" /> Support
        </p>
        <h1 className="font-display text-4xl font-extrabold leading-none tracking-tighter text-[#12142b] sm:text-5xl lg:text-6xl">
          Contact us
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#565b73]">
          Pick the topic that best matches your question and we'll get back to you as soon as we can.
          For emergencies, always contact local emergency services first.
        </p>
      </div>

      <main className="mx-auto max-w-[1000px] px-6 pb-24 lg:px-8">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {TOPICS.map((t) => (
            <a
              key={t.title}
              data-testid={`contact-topic-${t.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              href={`mailto:${EMAIL}?subject=${encodeURIComponent(t.subject)}`}
              className="group flex flex-col rounded-2xl border border-[#12142b]/8 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#2340b5]/10 lg:p-10"
            >
              <div className="flex items-center justify-between">
                <span className="grid h-12 w-12 place-items-center rounded-xl" style={{ backgroundColor: `${t.color}14` }}>
                  <t.icon className="h-6 w-6" strokeWidth={1.75} style={{ color: t.color }} />
                </span>
                <ArrowUpRight className="h-5 w-5 text-[#12142b]/25 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-[#2340b5]" />
              </div>
              <h2 className="mt-8 font-display text-xl font-bold tracking-tight text-[#12142b] lg:text-2xl">{t.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-[#565b73]">{t.body}</p>
              <span className="mt-6 inline-flex items-center gap-2 font-display text-sm font-semibold text-[#2340b5]">
                <Mail className="h-4 w-4" strokeWidth={1.75} /> {EMAIL}
              </span>
            </a>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-[#12142b]/8 bg-[#f4f6fb] p-8">
          <p className="font-display text-lg font-bold text-[#12142b]">Prefer to email us directly?</p>
          <p className="mt-3 text-base text-[#565b73]">
            Reach our support team any time at{" "}
            <a href={`mailto:${EMAIL}`} className="font-semibold text-[#2340b5] underline-offset-4 hover:underline">
              {EMAIL}
            </a>
            . Please include your account email and any relevant trip details so we can help faster.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
